import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTrialReleased } from "@/hooks/useTrialEligible";
import { TRIAL_DAYS } from "@/config/trial";

/**
 * The account step of the public site: a free signup, or the free trial
 * (TRIAL_DAYS) once it is released (Admin › Releases › free trial set to
 * Live). Until then nothing on the site mentions a trial.
 *
 * The site never quotes a price per audit or per credit here: a free account
 * is described by what it lets you do (two CRO audits), not by what it costs.
 */

/** Credits a free account starts with, and the CRO audits they cover. */
export const FREE_SIGNUP_CREDITS = 30;
export const FREE_SIGNUP_AUDITS = 2;

type Tr = (el: string, en: string) => string;

export function useStartFree() {
  const { language } = useLanguage();
  const tr: Tr = (el, en) => (language === "el" ? el : en);
  const trial = useTrialReleased();
  return {
    trial,
    /** Where the button goes: the plans (a trial starts on a plan) or the signup. */
    to: trial ? "/pricing" : "/signup",
    label: trial ? tr(`Δοκιμή ${TRIAL_DAYS} ημερών δωρεάν`, `Start ${TRIAL_DAYS}-day free trial`) : tr("Δωρεάν εγγραφή", "Sign up free"),
    /** The short version, for tight spots (the phone bar). */
    short: trial ? tr("Δοκιμή", "Free trial") : tr("Εγγραφή", "Sign up"),
    /** One line of facts under the buttons. */
    facts: trial
      ? [tr(`Δοκιμή ${TRIAL_DAYS} ημερών σε κάθε πλάνο`, `${TRIAL_DAYS}-day free trial on every plan`), tr("Ή δωρεάν εγγραφή, χωρίς κάρτα", "Or sign up free, no card"), tr("Το δείγμα ανοίγει χωρίς εγγραφή", "The sample opens without signing up")]
      : [tr("Δωρεάν εγγραφή, χωρίς κάρτα", "Free signup, no card"), tr(`${FREE_SIGNUP_AUDITS} CRO αναλύσεις δωρεάν`, `${FREE_SIGNUP_AUDITS} free CRO audits`), tr("Το δείγμα ανοίγει χωρίς εγγραφή", "The sample opens without signing up")],
    /** What the free account gives, in one sentence. */
    pitch: trial
      ? tr(`Κάθε πλάνο ξεκινά με δοκιμή ${TRIAL_DAYS} ημερών. Ακυρώνετε πριν την ${TRIAL_DAYS + 1}η μέρα και δεν πληρώνετε τίποτα.`, `Every plan starts with a ${TRIAL_DAYS}-day free trial. Cancel before day ${TRIAL_DAYS + 1} and you pay nothing.`)
      : tr(`Δωρεάν λογαριασμός με ${FREE_SIGNUP_CREDITS} credits, αρκετά για ${FREE_SIGNUP_AUDITS} CRO αναλύσεις. Χωρίς κάρτα.`, `A free account comes with ${FREE_SIGNUP_CREDITS} credits, enough for ${FREE_SIGNUP_AUDITS} CRO audits. No card.`),
  };
}

/** The signup or trial button. Green by default; "white" when it sits beside a green one. */
export function StartFreeLink({ variant = "primary", className, location }: { variant?: "primary" | "white"; className?: string; location?: string }) {
  const s = useStartFree();
  return (
    <Link
      to={s.to}
      data-track={s.trial ? "start_trial" : "start_signup"}
      data-location={location}
      className={cn(
        "inline-flex h-12 items-center justify-center gap-2 whitespace-nowrap rounded-[10px] px-5 text-[15px] font-semibold",
        variant === "primary" ? "ec-cta" : "ec-cta-white",
        className,
      )}
    >
      {s.label}
    </Link>
  );
}
